
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { Client } from "../types";
import { useState, useEffect, useMemo } from "react";

interface SearchDialogProps {
  clients?: Client[];
  onSelectClient?: (client: Client) => void;
  isLoading?: boolean;
  searchQuery?: string;
  onSearch?: (query: string) => void;
  placeholder?: string;
}

export const SearchDialog = ({ 
  clients = [], 
  onSelectClient, 
  isLoading = false,
  searchQuery = '',
  onSearch,
  placeholder = "Search clients..."
}: SearchDialogProps) => {
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);
  const [isOpen, setIsOpen] = useState(false);

  // Update local state when searchQuery prop changes
  useEffect(() => {
    setLocalSearchQuery(searchQuery);
  }, [searchQuery]);

  // Memoize filtered clients to prevent unnecessary recalculations
  const filteredClients = useMemo(() => {
    if (!localSearchQuery.trim() || !clients.length) {
      return [];
    }

    const query = localSearchQuery.toLowerCase();
    return clients.filter((client) => {
      const fullName = `${client.first_name || ''} ${client.last_name || ''}`.toLowerCase();
      const email = client.email?.toLowerCase() || '';
      const company = client.company_name?.toLowerCase() || '';
      
      return fullName.includes(query) || 
             email.includes(query) || 
             company.includes(query);
    });
  }, [localSearchQuery, clients]);

  const handleSelectClient = (client: Client) => {
    if (onSelectClient) {
      onSelectClient(client);
    }
    setIsOpen(false);
    setLocalSearchQuery('');
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalSearchQuery(newValue);
    if (onSearch) {
      onSearch(newValue);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2">
          <Search className="h-4 w-4" />
          Search Clients
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Search Clients</DialogTitle>
          <DialogDescription>
            Enter a name, email or company to search for a client.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <Input
            placeholder={placeholder}
            value={localSearchQuery}
            onChange={handleSearchChange}
            className="col-span-3"
            disabled={isLoading}
          />
          
          {onSelectClient && (
            <div className="max-h-[300px] overflow-auto space-y-2">
              {localSearchQuery && filteredClients.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  {isLoading ? "Loading..." : "No clients found matching your search."}
                </p>
              ) : (
                filteredClients.map((client) => (
                  <div
                    key={client.id}
                    className="p-2 hover:bg-gray-100 rounded cursor-pointer"
                    onClick={() => handleSelectClient(client)}
                  >
                    <p className="font-medium">
                      {client.first_name} {client.last_name}
                    </p>
                    <p className="text-sm text-gray-500">
                      {client.email}
                      {client.company_name && ` • ${client.company_name}`}
                    </p>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
